import React, { useMemo, useState } from 'react';
import { type Facet, DOMAIN_TO_STRATUM } from './Eisenboard/model';

interface StratumDiagnosisProps {
  facets: Facet[];
  userName?: string | null;
  problemTitle?: string;
  onDismiss?: () => void;
}

const INTERPRETATIONS: Array<{ primary: string; secondary: string; text: string }> = [
  { primary: 'L6', secondary: 'L4', text: 'A governance gap is causing coordination to break down.' },
  { primary: 'L6', secondary: 'L3', text: 'Trust and authority problems are showing up at the execution layer.' },
  { primary: 'L4', secondary: 'L6', text: 'Coordination breakdown — governance is not keeping pace.' },
  { primary: 'L4', secondary: 'L3', text: 'The orchestration layer is choking execution.' },
  { primary: 'L3', secondary: 'L4', text: 'Execution friction rooted in upstream coordination gaps.' },
  { primary: 'L3', secondary: 'L6', text: 'Execution problems with a governance risk underneath.' },
  { primary: 'L2', secondary: 'L5', text: 'The system lacks the memory and observability to operate consistently.' },
  { primary: 'L5', secondary: 'L3', text: 'You cannot see the problem clearly enough to fix the execution layer.' },
];

function getInterpretation(ranked: { short: string }[]): string {
  const [p, s] = ranked.map(r => r.short.split(' · ')[0]);
  const match = INTERPRETATIONS.find(i => i.primary === p && i.secondary === s);
  if (match) return match.text;
  if (p) return `This problem is primarily a ${ranked[0].short} challenge.`;
  return 'The board is mapping the problem across multiple layers.';
}

export const StratumDiagnosis: React.FC<StratumDiagnosisProps> = ({
  facets,
  userName,
  problemTitle = 'Your Challenge',
  onDismiss,
}) => {
  const [copied, setCopied] = useState(false);

  const stratumCounts = useMemo(() => {
    const counts: Record<string, { count: number; short: string; label: string; colorClass: string; colorHex: string }> = {};
    for (const facet of facets) {
      const s = DOMAIN_TO_STRATUM[facet.domain];
      if (!s) continue;
      const key = s.short;
      if (!counts[key]) counts[key] = { count: 0, short: s.short, label: s.label, colorClass: s.colorClass, colorHex: s.colorHex };
      counts[key].count += 1;
    }
    return Object.values(counts).sort((a, b) => b.count - a.count);
  }, [facets]);

  const maxCount = stratumCounts[0]?.count ?? 1;
  const interpretation = getInterpretation(stratumCounts);

  const handleCopy = () => {
    const lines = [
      `{a}OS7 Stratum Diagnosis`,
      `Problem: ${problemTitle}`,
      ``,
      `Your problem spans:`,
      ...stratumCounts.map((s, i) => {
        const rank = i === 0 ? '(Primary)' : i === 1 ? '(Secondary)' : '(Tertiary)';
        return `  ${s.short.padEnd(30)} ${rank}  ×${s.count}`;
      }),
      ``,
      interpretation,
      ``,
      `Powered by {a}OS7 Framework — aOS7.dev`,
    ].join('\n');
    void navigator.clipboard.writeText(lines).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleExport = () => {
    const rows = stratumCounts.map((s, i) => {
      const pct = Math.round((s.count / maxCount) * 100);
      const rank = i === 0 ? 'Primary' : i === 1 ? 'Secondary' : i === 2 ? 'Tertiary' : '';
      return `
        <div style="display:flex;align-items:center;gap:12px;margin-bottom:8px;">
          <div style="width:180px;font-size:11px;font-family:monospace;color:#94a3b8;white-space:nowrap;">${s.short}</div>
          <div style="flex:1;height:6px;background:#1e293b;border-radius:4px;overflow:hidden;">
            <div style="width:${pct}%;height:100%;background:${s.colorHex};border-radius:4px;"></div>
          </div>
          ${rank ? `<div style="width:64px;font-size:10px;color:#64748b;text-align:right;">${rank}</div>` : '<div style="width:64px;"></div>'}
        </div>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Stratum Diagnosis</title>
<style>body{margin:0;background:#050a14;color:#e2e8f0;font-family:-apple-system,sans-serif;display:flex;justify-content:center;padding:40px 16px;}
.card{max-width:520px;width:100%;background:#0d1420;border:1px solid rgba(255,255,255,0.08);border-radius:20px;padding:32px;}
h1{font-size:13px;letter-spacing:.2em;text-transform:uppercase;color:#475569;margin:0 0 8px;}
h2{font-size:18px;font-weight:700;color:#fff;margin:0 0 24px;}
.interpretation{margin-top:20px;padding:14px 16px;background:rgba(255,255,255,0.04);border-radius:10px;font-size:13px;line-height:1.6;color:#cbd5e1;}
.footer{margin-top:24px;font-size:10px;color:#334155;letter-spacing:.1em;text-transform:uppercase;}</style>
</head><body><div class="card">
<h1>{a}OS7 Stratum Diagnosis</h1>
<h2>${problemTitle}${userName ? ` — ${userName}` : ''}</h2>
<div>${rows}</div>
<div class="interpretation">${interpretation}</div>
<div class="footer">Powered by {a}OS7 Framework · aOS7.dev</div>
</div></body></html>`;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  };

  if (stratumCounts.length === 0) return null;

  return (
    <div className="mb-4 rounded-2xl border border-white/8 bg-[#080e1a] px-5 py-4 animate-in fade-in slide-in-from-top-2 duration-300">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-1">
            {'{a}OS7'} Stratum Diagnosis
          </div>
          <div className="text-sm font-semibold text-white">Your problem spans:</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={handleCopy}
            className="text-[10px] font-mono text-slate-400 hover:text-white transition-colors border border-white/10 rounded-lg px-2.5 py-1"
          >
            {copied ? '✓ copied' : 'copy'}
          </button>
          <button
            type="button"
            onClick={handleExport}
            className="text-[10px] font-mono text-cyan-400 hover:text-cyan-200 transition-colors border border-cyan-500/20 rounded-lg px-2.5 py-1"
          >
            export ↗
          </button>
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="text-slate-600 hover:text-slate-400 transition-colors text-lg leading-none"
              aria-label="Dismiss diagnosis"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {stratumCounts.map((s, i) => {
          const pct = Math.round((s.count / maxCount) * 100);
          const rank = i === 0 ? 'Primary' : i === 1 ? 'Secondary' : i === 2 ? 'Tertiary' : '';
          return (
            <div key={s.short} className="flex items-center gap-3">
              <div className={`w-36 shrink-0 text-[10px] font-mono truncate ${s.colorClass}`}>{s.short}</div>
              <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${pct}%`, backgroundColor: s.colorHex }}
                />
              </div>
              <div className="w-14 text-right text-[9px] font-mono text-slate-600">{rank}</div>
            </div>
          );
        })}
      </div>

      <p className="mt-4 text-xs text-slate-400 leading-relaxed border-l-2 border-white/10 pl-3">
        {interpretation}
      </p>
    </div>
  );
};
